import { Box, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import {
  DayConceptExclusions,
  getCompanyPaidLabel,
} from 'modules/expenses/components/expenses/New/PerDiemWizard/companyPaidUtils'
import { PerDiemDay } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'

export type { DayConceptExclusions }

interface BreakdownDayRowProps {
  day: PerDiemDay
  currency: string
  locale: string
  conceptExclusions?: DayConceptExclusions
}

const BreakdownDayRow = ({ day, currency, locale, conceptExclusions }: BreakdownDayRowProps) => {
  const baseRateConcepts = day.appliedConcepts.filter(
    (c) => c.type === 'full_day' || c.type === 'half_day'
  )
  const baseRateCents = baseRateConcepts.reduce((sum, c) => sum + c.conceptTotalCents, 0)

  const overnightConcept = day.appliedConcepts.find((c) => c.type === 'overnight')
  const overnightRateCents = Math.max(0, overnightConcept?.conceptTotalCents ?? 0)

  const totalDeductionsCents = day.appliedConcepts
    .filter((c) => c.type === 'full_day' || c.type === 'half_day')
    .reduce((total, concept) => {
      const conceptDeductions = concept.deductionsApplied.reduce(
        (sum, deduction) => sum + deduction.amountCents,
        0
      )
      return total + conceptDeductions
    }, 0)

  const formatMoney = (amount: number) =>
    isCurrency(currency) ? MoneyWithCurrency({ amount, currency, locale }) : '0'

  const companyPaidLabel = conceptExclusions ? getCompanyPaidLabel(conceptExclusions) : null

  return (
    <Box flexDirection="column" gap="s2">
      <Text.Normal size="200" weight="medium" color="grey1000">
        {moment(day.date).format('ddd, DD.MM.YYYY')}
      </Text.Normal>
      <Box paddingLeft="s4" flexDirection="column" gap="s1">
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Text.Normal size="200" weight="normal" color="grey700">
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.breakdown.base_rate')}
          </Text.Normal>
          <Text.Normal size="200" weight="medium" color="grey1000">
            {formatMoney(baseRateCents)}
          </Text.Normal>
        </Box>
        {overnightRateCents > 0 && (
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal size="200" weight="normal" color="grey700">
              {i18n.t('expenses_v2.forms.germany_per_diem_modal.breakdown.overnight_rate')}
            </Text.Normal>
            <Text.Normal size="200" weight="medium" color="grey1000">
              {formatMoney(overnightRateCents)}
            </Text.Normal>
          </Box>
        )}
        {totalDeductionsCents > 0 && (
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal size="200" weight="normal" color="grey700">
              {i18n.t('expenses_v2.forms.germany_per_diem_modal.breakdown.deductions')}
            </Text.Normal>
            <Text.Normal size="200" weight="medium" color="grey1000">
              {formatMoney(totalDeductionsCents)}
            </Text.Normal>
          </Box>
        )}
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          style={{ marginTop: 'var(--spacing-s2)' }}
        >
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.breakdown.day_total')}
          </Text.Normal>
          <Text.Normal size="200" weight="semibold" color="grey1000">
            {formatMoney(day.dayTotalCents)}
          </Text.Normal>
        </Box>
        {companyPaidLabel && (
          <Box style={{ marginTop: 'var(--spacing-s2)' }}>
            <Text.Normal size="200" color="grey700">
              {companyPaidLabel}
            </Text.Normal>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default BreakdownDayRow
